import type { AdditionalSelectors, VehicleIdentifier, VehicleSections } from '@makemydeal/dr-activities-vehicle-details';
import { getSalesCheckinBffEndPoint } from '@makemydeal/dr-sales-checkin-ui-utils';
import { canUseSubFeature, getActiveDeal, getActiveDealCommonOrgId, getCommonOrgId } from '@makemydeal/dr-sales-checkin-store';
import type { FinancialDetails, IDxgVehicleChromeStyleId } from '@makemydeal/dr-shared-types';
import type { VppInventoryType } from '@makemydeal/dr-platform-types';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { FEATURES } from '@makemydeal/dr-sales-checkin-types';

export const additionalSelectors: AdditionalSelectors = {
    getVehicleDetailsBffUrl: () => `${getSalesCheckinBffEndPoint('')}/vehicle/details`,

    getCommonOrgId: (state: any) => {
        // Prefer deal-level commonOrgId, fall back to dealer-level; return undefined if neither
        // so buildQueryParams omits dealerId from the query string rather than sending an empty value
        return getActiveDealCommonOrgId(state) || getCommonOrgId(state) || undefined;
    },

    getVehicle: (state: any): VehicleIdentifier | undefined => {
        const activeDeal = getActiveDeal(state);
        const sellingVehicle = activeDeal?.dealStructure?.vehicles?.sellingVehicle;
        const priceCostDetails = sellingVehicle?.priceCostDetails;

        if (!sellingVehicle) return undefined;
        const chromeStyleId = sellingVehicle.vehicleCodes
            ?.find((code): code is IDxgVehicleChromeStyleId => code.codeType === 'ChromeStyleId')
            ?.chromeStyleId?.toString();
        return {
            vin: sellingVehicle.vin,
            stockNumber: sellingVehicle.stockNumber,
            isCustom: sellingVehicle.isCustom,
            chromeStyleId,
            odometerMileage: sellingVehicle.odometerMileage,
            condition: sellingVehicle.inventoryType as VppInventoryType,
            priceCostDetails: priceCostDetails,
            vehicleImage: sellingVehicle.vehicleImage
        };
    },

    getIsEnhancedShowroomMode: (state: any): boolean => featureToggleSelectors.isEnhancedShowroomModeEnabled(state),

    getIsAllVehicleInventoryFromCVIEnabled: (state: any): boolean =>
        featureToggleSelectors.isAllVehicleInventoryFromCVIEnabled(state),

    getIsFullPageScrollEnabled: (state: any): boolean => featureToggleSelectors.isSciFullPageScrollEnabled(state),

    /**
     * Which VDP sections this edition allows, resolved here so the VDP activity stays
     * capability-agnostic: it reads booleans, exactly as it does for `getIsEnhancedShowroomMode`.
     * This is also the single place the two gates on those sections compose — the enhanced-mode
     * toggle still applies inside the activity.
     *
     * Held at all-on for now: the BFF does not send `capabilities` yet, so the slice is empty and
     * the real read would resolve every section to false, blanking the VDP.
     */
    getVehicleSections: (state: any): VehicleSections => ({
        images: canUseSubFeature(FEATURES.VEHICLE_DETAILS, 'images')(state),
        details: canUseSubFeature(FEATURES.VEHICLE_DETAILS, 'details')(state),
        optionalEquipment: canUseSubFeature(FEATURES.VEHICLE_DETAILS, 'optionalEquipment')(state),
        estimatedMPG: canUseSubFeature(FEATURES.VEHICLE_DETAILS, 'estimatedMPG')(state),
        warranty: canUseSubFeature(FEATURES.VEHICLE_DETAILS, 'warranty')(state)
    }),
    getFinancialDetails: function (state: any): FinancialDetails | undefined {
        return getActiveDeal(state)?.dealStructure?.financialDetails;
    }
};
